import { blogFiles } from "../../blog_posts";
import type {
  BlogListItemProps,
  FullBlogPostProps,
  RawBlogPost,
} from "./types";

function isPreviewBuild(): boolean {
  const prerenderEnv = (globalThis as { __PRERENDER_ENV__?: unknown }).__PRERENDER_ENV__;
  if (typeof prerenderEnv === "string") {
    return prerenderEnv !== "production";
  }

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1" ||
      hostname === "[::1]"
    );
  }

  return false;
}

function normalizeLegacyActiveDate(activeDate: unknown): string | null {
  if (typeof activeDate === "string") {
    const trimmed = activeDate.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (activeDate instanceof Date) {
    return Number.isNaN(activeDate.valueOf()) ? null : activeDate.toISOString();
  }

  if (typeof activeDate === "boolean") {
    return activeDate ? "true" : null;
  }

  return null;
}

function resolveActiveDateTimestamp(activeDate: unknown): number | null {
  const normalized = normalizeLegacyActiveDate(activeDate);
  if (normalized === null || normalized === "true") {
    return null;
  }

  const timestamp = new Date(normalized).valueOf();
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function isRawBlogPostActive(rawPost: RawBlogPost, now: Date = new Date()): boolean {
  if (rawPost.id === "template") {
    return false;
  }

  const activeDateTimestamp = resolveActiveDateTimestamp(rawPost.active_date);
  if (activeDateTimestamp !== null) {
    return activeDateTimestamp < now.valueOf() || isPreviewBuild();
  }

  return rawPost.active === true || normalizeLegacyActiveDate(rawPost.active_date) === "true";
}

function getSortedActiveBlogPosts(): RawBlogPost[] {
  const posts = blogFiles as RawBlogPost[];

  return posts
    .reduce<RawBlogPost[]>((accum, rawPost) => {
      if (isRawBlogPostActive(rawPost)) {
        accum.push(rawPost);
      }

      return accum;
    }, [])
    .sort(
      (postA: RawBlogPost, postB: RawBlogPost) =>
        new Date(postB.publish_date).valueOf() -
        new Date(postA.publish_date).valueOf(),
    );
}

const activeSortedRawPosts = getSortedActiveBlogPosts();

export const blogMeta = activeSortedRawPosts.reduce<
  Record<string, { title: string; description: string }>
>((accum, post) => {
  accum[`/blog/${post.id}`] = {
    title: post.meta_title ?? `${post.title} | MyDecisive`,
    description:
      post.meta_description ??
      post.short_description ??
      post.blog_focus ??
      post.body.slice(0, 150),
  };
  return accum;
}, {});

export const blogPosts = activeSortedRawPosts
  .map(rawBlogPostToBlogListItem)
  .map((item) => ({
    ...item,
    link: `/blog/${item.id}`,
    image: item.blogImage,
    imageAlt: `Teaser image for post: ${item.title}`,
    date: item.publishDate,
    description: item.shortDescription,
    avatar: item.authorImage,
    avatarAlt: `blog author: ${item.author}`,
  }));

function rawBlogPostToBlogListItem(rawPost: RawBlogPost): BlogListItemProps {
  const {
    author,
    author_image,
    id,
    publish_date,
    short_description,
    teaser_image,
    title,
    body,
    blog_focus,
  } = rawPost;

  return {
    author,
    authorImage: author_image,
    id,
    publishDate: publish_date,
    shortDescription:
      short_description ??
      blog_focus ??
      body.split(" ").slice(0, 100).join(" ").concat("..."),
    blogImage: rawPost.blog_image ?? "Octopus.png",
    title,
    teaserImage: teaser_image,
  };
}

export const blogPageContent = activeSortedRawPosts.reduce<
  Record<string, FullBlogPostProps>
>((acc, current, index, arr) => {
  const almostFullPost = rawBlogPostToFullBlogPost(current);

  acc[almostFullPost.id] = {
    ...almostFullPost,
    previousPost: index === 0 ? null : `/blog/${arr[index - 1].id}`,
    nextPost: index === arr.length - 1 ? null : `/blog/${arr[index + 1].id}`,
  };

  return acc;
}, {});

function rawBlogPostToFullBlogPost(
  rawPost: RawBlogPost,
): Omit<FullBlogPostProps, "previousPost" | "nextPost"> {
  const {
    author,
    author_about,
    author_image,
    body,
    blog_focus,
    blog_image,
    id,
    publish_date,
    subtitle,
    tags = [],
    title,
  } = rawPost;

  return {
    author,
    authorAbout: author_about,
    authorImage: author_image,
    body,
    blogFocus: blog_focus,
    blogImage: blog_image ?? "left-desktop-banner.png",
    id,
    publishDate: publish_date,
    subtitle,
    tags,
    title,
  };
}
